/**
 * Arrow-key navigation for a list whose options are server-rendered DOM rather than a JS array.
 *
 * The active option is tracked by ELEMENT ID, not by index. Every other active-index widget here
 * (select2, select2-server, select-multi-server) indexes into a JavaScript array it owns, so an
 * index is stable. Where a client-side filter hides rows underneath the highlight, an index
 * silently repoints at a different option the moment the user types another character. An id also
 * self-heals: a highlighted option the filter hides simply stops matching, so no stale highlight
 * can render, and it is exactly what aria-activedescendant needs with no lookup table.
 *
 * CONTRACT FOR CALLERS — this is consumed by SPREADING into an x-data object literal:
 *   x-data="{ ...window.listKeyboardNav({ listRef: 'list' }), ...your own state }"
 * so it must never declare a getter ({...obj} copies a getter's VALUE at spread time, not the
 * getter) and must never declare init() (the caller's own init() would silently win). It reserves
 * the names `activeId` and `nav*`. The caller supplies the x-ref on the scroll container, a unique
 * id on every option, the key bindings, the highlight class and every reset trigger.
 *
 * The IME and modifier guards live in here rather than at the call site so that correctness
 * travels with the helper instead of being re-derived, and got wrong, by the next list.
 *
 * `isSelectable` is REQUIRED and deliberately has no default. The obvious default is a layout read
 * (`offsetParent !== null`), and that is a trap: offsetParent only tells the truth once the
 * framework has flushed its show/hide effects, so any caller that recomputes the active option in
 * response to the same state its filter reads is racing those effects. Losing that race leaves the
 * active id naming a row that is about to become display:none — for a combobox, a dangling
 * aria-activedescendant pointing at something nobody can see. That is not hypothetical: it is what
 * the first consumer of this helper shipped, intermittently, until it was caught. Shipping it as a
 * default would hand the same bug to the next list silently, so the question is asked out loud
 * instead. Prefer a pure data test — ask the filter the same question the filter asks. For a list
 * nothing can hide underneath the highlight, `byLayout` below is the honest version of the old
 * default, and naming it is the point.
 *
 * @param {string}   listRef         x-ref of the scrolling container that holds the options.
 * @param {string}   optionSelector  CSS selector for the options, scoped to that container.
 * @param {function} isSelectable    (option) => boolean, called with the component as `this`.
 */
export const byLayout = (option) => option.offsetParent !== null && ! option.disabled;

export default ({ listRef = 'list', optionSelector = '[role=option]', isSelectable } = {}) => {
    if (typeof isSelectable !== 'function') {
        throw new Error('listKeyboardNav: isSelectable(option) is required. Pass a data test, or byLayout for a list no filter can hide rows in.');
    }

    return {
    activeId: null,

    /** Called with the component as `this`, so a data test can reach the caller's own state. */
    navIsSelectable(option) {
        return isSelectable.call(this, option);
    },

    /**
     * Hands the highlight off to real DOM focus, so Tab goes where the ring is rather than to the
     * top of the list. Without it, arrowing to the fourth row and pressing Tab landed on the FIRST
     * row's controls while the ring vanished - the keyboard contradicting what the eye was told.
     * This is a handoff, not a second live focus model: the listbox's @focusin clears activeId the
     * moment focus lands, so the highlight and DOM focus are still never live at the same time.
     * Shift+Tab is left alone - it is the way back out of the list.
     */
    navFocusActive(event) {
        if (event.shiftKey) {
            return;
        }

        const option = this.navActiveOption();

        if (! option) {
            return;
        }

        event.preventDefault();
        option.focus();
    },

    /** The options a keypress may land on, in DOM order. */
    navOptions() {
        const list = this.$refs[listRef];

        if (! list) {
            return [];
        }

        return [...list.querySelectorAll(optionSelector)]
            .filter((option) => this.navIsSelectable(option));
    },

    navActiveOption() {
        return this.navOptions().find((option) => option.id === this.activeId) ?? null;
    },

    navSetActive(option) {
        this.activeId = option?.id ?? null;

        if (option) {
            option.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    },

    navFirst() {
        this.navSetActive(this.navOptions()[0] ?? null);
    },

    navClear() {
        this.activeId = null;
    },

    /**
     * Wraps at both ends. From no active option, +1 lands on the first and -1 on the last, which is
     * what makes the first ArrowUp reach the bottom of the list rather than its second-to-last row.
     *
     * preventDefault() is called here rather than through Alpine's .prevent modifier because .prevent
     * runs BEFORE the handler body: it would swallow the arrow key an IME needs to walk its candidate
     * window even though the guard below bails out. The guard has to come first and preventDefault
     * second, which the modifier cannot express.
     */
    navMove(event, delta) {
        if (event.isComposing || event.keyCode === 229) {
            return;
        }

        // Modifier+Arrow belongs to the caret (select-to-end, macOS move-to-end), and Alt+Down is
        // the APG key that OPENS a combobox popup - a popup that has no collapsed state of its own
        // is correct to do nothing rather than treat it as a second ArrowDown.
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }

        event.preventDefault();

        const options = this.navOptions();

        if (! options.length) {
            this.navClear();

            return;
        }

        const from = options.findIndex((option) => option.id === this.activeId);
        const next = from === -1
            ? (delta > 0 ? 0 : options.length - 1)
            : (from + delta + options.length) % options.length;

        this.navSetActive(options[next]);
    },

    /**
     * click(), not requestSubmit() or a bespoke handler: it is the identical path a mouse click
     * already takes, so keyboard and mouse activation cannot drift apart, and it is inert on a
     * disabled control if an id ever goes stale.
     */
    navActivate(event) {
        if (event.isComposing || event.keyCode === 229) {
            return;
        }

        const option = this.navActiveOption();

        if (! option) {
            return;
        }

        event.preventDefault();
        option.click();
    },
    };
};
